/**
 * @module cercaliagl/service/Isochrone
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import Service from './Service';
/**
 * @typedef {Object} IsochroneOptions
 * @property {string} [countryId] Country code filter
 * @property {string} [country] Country name filter
 * @property {string} [regionId] Region code filter
 * @property {string} [region] Region name filter
 * @property {string} [subregionId] Subregion code filter
 * @property {string} [subregion] Subregion name filte
 * @property {string} [subOrRegion] Region and Subregion name filter
 * @property {string} [municipality] Municipality code filter
 * @property {string} [municipalityId] Municipality name filter
 * @property {string} [cityId] Town code filter
 * @property {string} [city] Town name filter
 * @property {import('../LonLat').default} [position] Position
 * @property {string} [postalCode] Postal code filter
 * @property {string} [address] Street name filter with house number
 * @property {string} [streetId] Street code filter
 * @property {string} [street] Street name filter
 * @property {number} [housenumber] House number filter
 * @property {string} [roadName] Road name filter
 * @property {string} [roadId] Road id filter
 * @property {string} [weight] Proximity with real route distance. Possible values: `time`, `distance`, `money`.
 * @property {boolean} [inverse] Can be true or false. With a false value, the distances are calculated from the reference to the
 *  target. With a true value, the distances are calculated from the references to the targets. The default is false.
 * @property {string} [method] Calculation method. Por defecto: `convexhull` or `concavehull`. Default value `convexhull`.
 * @property {number} [isolevels] Value, or incremental values list used for isochrone calculation (meters or miliseconds). Ex: `10000`, `5000,10000,20000`.
 * @property {number} [net] Calculation net: car, bike or foot. Default value `car`.
 */
/**
 * @api
 * @type {Object.<string, string>}
 */
export var Weight = {
    /**
     * @api
     */
    DISTANCE: 'distance',
    /**
     * @api
     */
    TIME: 'time',
};
/**
 * @api
 * @type {Object.<string, string>}
 */
export var Methods = {
    /**
     * @api
     */
    SIMPLE: 'convexhull',
    /**
     * @api
     */
    DETAILED: 'concavehull',
};
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const isochroneService = new cercaliagl.service.Isochrone({ ... });
 * ```
 *
 * This class offers the service of calculating isochrones given an origin point, weight and value.<br/<
 * It also allows to obtain a detailed or simpler geometry.<br/>
 * <br/>
 * Example usage:
 *
 * ```js
 * const isochroneService = new cercaliagl.service.Isochrone({
 *   position: new cercaliagl.LonLat(2.153665978, 41.395293973);
 *   weight: cercaliagl.service.Isochrone.Weight.DISTANCE,
 *   method: cercaliagl.service.Isochrone.Methods.DETAILED,
 *   isolevels: 20000
 * });
 *
 * isochroneService.calculate((data) => {
 *   const wkt = data.cercalia.isochrones.isochrone.value;
 *   const feature = new cercaliagl.Feature({
 *     wkt: wkt
 *   });
 *   map.addFeature(feature);
 *   map.centerToFeatures([feature]);
 * });
 * ```
 *
 * @param {IsochroneOptions} [options] Isochrone request options
 * @extends {Service}
 * @api
 */
var Isochrone = /** @class */ (function (_super) {
    __extends(Isochrone, _super);
    /**
     * @constructor
     * @param {IsochroneOptions} options
     */
    function Isochrone(options) {
        if (options === void 0) { options = {}; }
        var _this = _super.call(this) || this;
        /**
         * @type {Object}
         * @private
         */
        _this.response_ = null;
        /**
         * Country code filter (ctryc)
         * @private
         * @type {string|null}
         */
        _this.countryId_ = options.countryId ? options.countryId : null;
        /**
         * Country name filter (ctryn)
         * @private
         * @type {string|null}
         */
        _this.country_ = options.country ? options.country : null;
        /**
         * Region code filter (regc)
         * @private
         * @type {string|null}
         */
        _this.regionId_ = options.regionId ? options.regionId : null;
        /**
         * Region name filter (regn)
         * @private
         * @type {string|null}
         */
        _this.region_ = options.region ? options.region : null;
        /**
         * Subregion code filter (subregc)
         * @private
         * @type {string|null}
         */
        _this.subregionId_ = options.subregionId ? options.subregionId : null;
        /**
         * Subregion name filter (subregn)
         * @private
         * @type {string|null}
         */
        _this.subregion_ = options.subregion ? options.subregion : null;
        /**
         * Region and Subregion name filter (rsn)
         * @private
         * @type {string|null}
         */
        _this.subOrRegion_ = options.subOrRegion ? options.subOrRegion : null;
        /**
         * Municipality code filter (munc)
         * @private
         * @type {string|null}
         */
        _this.municipalityId_ = options.municipalityId ? options.municipalityId : null;
        /**
         * Municipality name filter (munn)
         * @private
         * @type {string|null}
         */
        _this.municipality_ = options.municipality ? options.municipality : null;
        /**
         * Town code filter (ctc)
         * @private
         * @type {string|null}
         */
        _this.cityId_ = options.cityId ? options.cityId : null;
        /**
         * Town name filter (ctn)
         * @private
         * @type {string|null}
         */
        _this.city_ = options.city ? options.city : null;
        /**
         * Postal code filter (pcode)
         * @private
         * @type {string|null}
         */
        _this.postalCode_ = options.postalCode ? options.postalCode : null;
        /**
         * Street code filter (stc)
         * @private
         * @type {string|null}
         */
        _this.streetId_ = options.streetId ? options.streetId : null;
        /**
         * Street name filter (stn)
         * @private
         * @type {string|null}
         */
        _this.street_ = options.street ? options.street : null;
        /**
         * House number filter (stnum)
         * @private
         * @type {number|null}
         */
        _this.housenumber_ = options.housenumber ? options.housenumber : null;
        /**
         * Street name filter with house number
         * @private
         * @type {string|null}
         */
        _this.address_ = options.address ? options.address : null;
        /**
         * Position of center to calculate isochrone request.
         * @private
         * @type {import('../LonLat').default|null}
         */
        _this.position_ = options.position ? options.position : null;
        /**
         * Type of search value. Possible values `distance` or `time`. Default value  `distance`
         * @private
         * @type {string}
         */
        _this.weight_ = options.weight ? options.weight : Weight.DISTANCE;
        /**
         * If `false`, the calculation is from the center to outside. If `true`, the calculation is from outside to the center. Default value `false`.
         * @private
         * @type {boolean}
         */
        _this.inverse_ = options.inverse ? options.inverse : false;
        /**
         * Calculation method. Por defecto: `convexhull` or `concavehull`. Default value `convexhull`.
         * @private
         * @type {string}
         */
        _this.method_ = options.method ? options.method : Methods.DETAILED;
        /**
         * Value, or incremental values list used for isochrone calculation (meters or miliseconds). Ex: `10000`, `5000,10000,20000`.
         * @private
         * @type {number|null}
         */
        _this.isolevels_ = options.isolevels ? options.isolevels : null;
        /**
         * Calculation net: car, bike or foot. Default value `car`.
         * @private
         * @type {string|null}
         */
        _this.net_ = options.net ? options.net : null;
        return _this;
    }
    /**
     * Use null to all properties.
     */
    Isochrone.prototype.clear = function () {
        this.countryId_ = null;
        this.country_ = null;
        this.regionId_ = null;
        this.region_ = null;
        this.subregionId_ = null;
        this.subregion_ = null;
        this.subOrRegion_ = null;
        this.municipalityId_ = null;
        this.municipality_ = null;
        this.cityId_ = null;
        this.city_ = null;
        this.postalCode_ = null;
        this.address_ = null;
        this.streetId_ = null;
        this.street_ = null;
        this.housenumber_ = null;
        this.weight_ = Weight.DISTANCE;
        this.inverse_ = false;
        this.method_ = Methods.DETAILED;
        this.isolevels_ = null;
        this.response_ = null;
    };
    /**
     * @param {IsochroneOptions} options Options
     */
    Isochrone.prototype.setSearch = function (options) {
        this.countryId_ = options.countryId ? options.countryId : null;
        this.country_ = options.country ? options.country : null;
        this.regionId_ = options.regionId ? options.regionId : null;
        this.region_ = options.region ? options.region : null;
        this.subregionId_ = options.subregionId ? options.subregionId : null;
        this.subregion_ = options.subregion ? options.subregion : null;
        this.subOrRegion_ = options.subOrRegion ? options.subOrRegion : null;
        this.municipalityId_ = options.municipalityId ? options.municipalityId : null;
        this.municipality_ = options.municipality ? options.municipality : null;
        this.cityId_ = options.cityId ? options.cityId : null;
        this.city_ = options.city ? options.city : null;
        this.postalCode_ = options.postalCode ? options.postalCode : null;
        this.address_ = options.address ? options.address : null;
        this.streetId_ = options.streetId ? options.streetId : null;
        this.street_ = options.street ? options.street : null;
        this.housenumber_ = options.housenumber ? options.housenumber : null;
        this.weight_ = options.weight ? options.weight : Weight.DISTANCE;
        this.inverse_ = options.inverse ? options.inverse : false;
        this.method_ = options.method ? options.method : Methods.DETAILED;
        this.isolevels_ = options.isolevels ? options.isolevels : null;
        this.position_ = options.position ? options.position : null;
        this.net_ = options.net ? options.net : null;
        this.response_ = null;
    };
    /**
     * Returns the response once the 'isochrone' is calculated, before that it will return `null`.
     * @return {Object} response
     */
    Isochrone.prototype.getResponse = function () {
        return this.response_;
    };
    /**
     * Do isochrone request.
     * @param {Function} callbackFn Callback Function
     * @param {Function} callbackFnError Callback Function if there is an error
     */
    Isochrone.prototype.calculate = function (callbackFn, callbackFnError) {
        var _this = this;
        var params = { cmd: 'isochrone' };
        if (this.countryId_) {
            params.countryId = this.countryId_;
        }
        if (this.country_) {
            params.country = this.country_;
        }
        if (this.regionId_) {
            params.regionId = this.regionId_;
        }
        if (this.region_) {
            params.region = this.region_;
        }
        if (this.subregionId_) {
            params.subregionId = this.subregionId_;
        }
        if (this.subregion_) {
            params.subregion = this.subregion_;
        }
        if (this.subOrRegion_) {
            params.subOrRegion = this.subOrRegion_;
        }
        if (this.municipalityId_) {
            params.municipalityId = this.municipalityId_;
        }
        if (this.municipality_) {
            params.municipality = this.municipality_;
        }
        if (this.cityId_) {
            params.cityId = this.cityId_;
        }
        if (this.city_) {
            params.city = this.city_;
        }
        if (this.postalCode_) {
            params.postalCode = this.postalCode_;
        }
        if (this.address_) {
            params.address = this.address_;
        }
        if (this.streetId_) {
            params.streetId = this.streetId_;
        }
        if (this.street_) {
            params.street = this.street_;
        }
        if (this.housenumber_) {
            params.housenumber = this.housenumber_;
        }
        if (this.weight_) {
            params.weight = this.weight_;
        }
        if (this.inverse_) {
            params.inverse = this.inverse_ ? '1' : '0';
        }
        if (this.method_) {
            params.method = this.method_;
        }
        if (this.isolevels_) {
            params.isolevels = this.isolevels_;
        }
        if (this.position_) {
            params.mo = "".concat(this.position_.getLat(), ",").concat(this.position_.getLon());
        }
        if (this.net_) {
            params.net = this.net_;
        }
        this.getRequest({
            data: params,
            timeout: 50000,
            callbackFn: function (data) {
                _this.response_ = data;
                if (callbackFn) {
                    callbackFn(_this.response_);
                }
                else {
                    console.error('No callbackfunction defined'); // eslint-disable-line no-console
                }
            },
            callbackFnError: function (error) {
                if (callbackFnError) {
                    callbackFnError(error);
                }
            },
        });
    };
    return Isochrone;
}(Service));
export default Isochrone;
//# sourceMappingURL=Isochrone.js.map